// Match by Login (primary) or Full_Name (fallback). Do not hardcode Employee_ID (DEV/PROD differ).
//
// PRM group (§2/§3 targets) — 15 members:
//   alinap, andriyd, bohdanb, dsemen, diman, elina.lapka, igorb, igork,
//   oleh.hlushko, romanch, roman.zherukha, slaval, taras.vons, tarasz, vmatv

const FULL_ACCESS_LOGINS = new Set([
  'alinap',
  'dsemen',
  'diman',
  'romanch',
  'tarasz',
  'vmatv'
])

const PRM_GROUP_LOGINS = new Set([
  'alinap',
  'andriyd',
  'bohdanb',
  'dsemen',
  'diman',
  'elina.lapka',
  'igorb',
  'igork',
  'oleh.hlushko',
  'romanch',
  'roman.zherukha',
  'slaval',
  'taras.vons',
  'tarasz',
  'vmatv'
])

const FULL_ACCESS_NORMALIZED_NAMES = new Set([
  'dmytro pavlov',
  'dima semensky',
  'roman chapko',
  'taras zherukha',
  'vitaliy matvisiv',
  'alina pavlova'
])

const PRM_GROUP_NORMALIZED_NAMES = new Set([
  'alina pavlova',
  'andriy dovgal',
  'bohdan boyko',
  'dmytro pavlov',
  'dima semensky',
  'igor bilokuryy',
  'ihor kruzhylko',
  'elina lapka',
  'oleh hlushko',
  'roman chapko',
  'roman zherukha',
  'svyatoslav lavryk',
  'taras vons',
  'vitaliy matvisiv',
  'taras zherukha'
])

export const AM_STATUS = 'Account Manager (AM)'

export const PM_PC_STATUSES = [
  'Project Manager (PM)',
  'PM',
  'Project Manager',
  'Project Coordinator (PC)',
  'PC',
  'Project Coordinator',
  'Director of Project Management Office'
]

export const HRM_STATUSES = ['Office Manager / HRM', 'HR Manager']

const ROMAN_ZHERUKHA_LOGIN = 'roman.zherukha'
const STALE_ROMAN_ZHERUKHA_LOGIN = 'romanz'

function normalizeEmployeeName (employee) {
  if (!employee) return ''
  if (employee.Full_Name) {
    return employee.Full_Name.toLowerCase().trim()
  }
  return `${employee.Name || ''} ${employee.Last_Name || ''}`.toLowerCase().trim()
}

function normalizeLogin (employee) {
  if (!employee || !employee.Login) return ''
  return employee.Login.toLowerCase().trim()
}

function matchesEmployeeInSet (employee, nameSet, loginSet) {
  if (!employee) return false

  const login = normalizeLogin(employee)
  if (login === STALE_ROMAN_ZHERUKHA_LOGIN) {
    return false
  }
  if (login && loginSet.has(login)) {
    return true
  }

  const name = normalizeEmployeeName(employee)
  if (name === 'roman zherukha') {
    return login === ROMAN_ZHERUKHA_LOGIN
  }
  return nameSet.has(name)
}

function isRestrictedManagementStatus (employee) {
  const status = employee && employee.Status
  if (!status) return false
  if (status === AM_STATUS) return true
  return HRM_STATUSES.includes(status)
}

export function hasFullAccess (viewerEmployee) {
  return matchesEmployeeInSet(viewerEmployee, FULL_ACCESS_NORMALIZED_NAMES, FULL_ACCESS_LOGINS)
}

export function isAccountManager (employee) {
  return employee && employee.Status === AM_STATUS
}

export function isPMorPC (employee) {
  return employee && PM_PC_STATUSES.includes(employee.Status)
}

export function isHrOrHrmViewer (viewerEmployee) {
  const status = viewerEmployee && viewerEmployee.Status
  return HRM_STATUSES.includes(status)
}

export function isInPrmGroup (employee) {
  if (!employee) return false
  if (matchesEmployeeInSet(employee, PRM_GROUP_NORMALIZED_NAMES, PRM_GROUP_LOGINS)) {
    return true
  }
  return isRestrictedManagementStatus(employee)
}

function buildAccessContext ({
  viewerEmployeeId,
  targetEmployee,
  viewerEmployee
}) {
  const isSelf = viewerEmployeeId === targetEmployee.Employee_ID
  const fullAccess = hasFullAccess(viewerEmployee)
  const targetInPrmGroup = isInPrmGroup(targetEmployee)
  const viewerIsAm = isAccountManager(viewerEmployee)
  const viewerIsPmPc = isPMorPC(viewerEmployee)
  const viewerIsHrHrm = isHrOrHrmViewer(viewerEmployee)
  const isSupervisor = targetEmployee.Supervisor_ID === viewerEmployeeId

  return {
    isSelf,
    fullAccess,
    targetInPrmGroup,
    viewerIsAm,
    viewerIsPmPc,
    viewerIsHrHrm,
    isSupervisor
  }
}

export function canViewPrm ({
  viewerEmployeeId,
  targetEmployee,
  viewerEmployee
}) {
  if (!targetEmployee || !targetEmployee.Employee_ID) {
    return false
  }

  const ctx = buildAccessContext({ viewerEmployeeId, targetEmployee, viewerEmployee })

  if (ctx.isSelf || ctx.fullAccess) {
    return true
  }

  if (ctx.viewerIsHrHrm) {
    return !ctx.targetInPrmGroup
  }

  // §4 before §3 — PC/PM keep broad access even if also on PRM list
  if (ctx.viewerIsPmPc) {
    return true
  }

  if (ctx.viewerIsAm) {
    return !ctx.targetInPrmGroup
  }

  if (ctx.isSupervisor) {
    return true
  }

  return false
}

export function canViewSummary ({
  viewerEmployeeId,
  targetEmployee,
  viewerEmployee
}) {
  if (!canViewPrm({ viewerEmployeeId, targetEmployee, viewerEmployee })) {
    return false
  }

  const ctx = buildAccessContext({ viewerEmployeeId, targetEmployee, viewerEmployee })

  if (ctx.isSelf || ctx.fullAccess) {
    return true
  }

  if (ctx.viewerIsPmPc) {
    return false
  }

  if (ctx.viewerIsHrHrm || ctx.viewerIsAm) {
    return true
  }

  if (ctx.isSupervisor) {
    return false
  }

  return false
}
